import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../../../../store";

import {
  Modal,
  Button,
  Spinner,
  Row,
  Col,
  Form,
  Overlay,
  OverlayTrigger,
  Popover,
  Tooltip,
} from "react-bootstrap";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import {
  hideGroupReorderModal,
} from "../../../../../../store/onboarding/actions";
import useUserRole from "../../../../../../utils/hooks/useUserRole";
import { toastr } from "react-redux-toastr";
import { Student } from "../../../../../../store/onboarding/types";
import {
  GroupRecommendation,
  InterventionGroup,
  StudentGoal,
} from "../../../../../../store/onboarding/cases/types";
import { UserAssignmentRole, UserInfo } from "../../../../../../store/auth/types";
import { group } from "console";
import { groupReorderUpdate } from "../../../../../../store/auth/actions";
import useInterventionGroupReorder from "./hooks/useInterventionGroupReorder";
import { color } from "html2canvas/dist/types/css/types/color";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical, faSync } from "@fortawesome/free-solid-svg-icons";
import LoadingIndicator from "../../../../../common/onboarding/LoadingIndicator";
import useDeviceDetect from "../../../../../../utils/hooks/useDeviceDetect";
import { uniqueByKey } from "../../../../../../utils/NamesUtils";

type OwnProps = {
  student?: Student;
};

type Props = OwnProps;

type SortableItemProps = {
  group: InterventionGroup;
  selectedIds: number[];
  toggleSelect: (id: number) => void;
  activeId: number | null;
  popoverOpenId: number | null;
  setPopoverOpenId: (id: number | null) => void;
};

const SortableItem = ({
  group,
  selectedIds,
  toggleSelect,
  activeId,
  popoverOpenId,
  setPopoverOpenId,
}: SortableItemProps) => {
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get('tab');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: group.id,
  });

  const isMultiDragging =
    activeId &&
    selectedIds.includes(activeId) &&
    selectedIds.includes(group.id);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: group.finished
      ? "#00265b"
      : "#43a047",

    color: "#fff",
    opacity: isMultiDragging ? 0 : 1,
  };

  const filterStudents = (
      selectedInterventionGroup: InterventionGroup | undefined
    ) => {
    let students: Student[] | undefined = (urlTab == "archived")
      ?
        selectedInterventionGroup?.student_goals
          .filter((sg: StudentGoal) => sg.archived)
          .map((sg: StudentGoal) => sg.student) || []
      :
      selectedInterventionGroup?.students.filter((student: Student) => {
          return selectedInterventionGroup?.student_goals.some(
            (sg: StudentGoal) => (student.id == sg.student.id) && (urlTab == "archived")
                        ? sg.archived : !sg.archived)
          } 
        )
    let studentsList = selectedInterventionGroup?.students.filter(
      (student: Student, index: number) => {
        let studentGoal: StudentGoal | undefined;
        if (selectedInterventionGroup?.student_goals) {
            studentGoal = selectedInterventionGroup?.student_goals.find(
              (x) => x.student.id === student.id && (urlTab == "archived" ? x.archived : !x.archived)
            );
        }
        return !!studentGoal;
      }
    );

    return urlTab == "archived" ? 
            uniqueByKey([...studentsList!, ...(students || [])], 'id') 
            : uniqueByKey([...studentsList!], 'id');
  };

  const isPopoverOpen = popoverOpenId === group.id;
  const badgeRef = React.useRef<HTMLDivElement | null>(null);

  return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className={`groupCard
          ${isDragging ? "dragging" : ""}
          ${
            selectedIds.includes(group.id)
              ? "selected"
              : ""
          }
        `}
      >
        <div className="groupCardLeft">
          <div
            className="dragHandle"
            {...attributes}
            {...listeners}
          >
            <div className="dragGrip">
              <FontAwesomeIcon
                icon={faGripVertical}
                size="1x"
                style={{ verticalAlign: "middle", color: "#ccc" }}
              />
            </div>
            <Form.Check
              type="checkbox"
              custom
              checked={selectedIds.includes(
                group.id
              )}
              onChange={() =>
                toggleSelect(group.id)
              }
              className="groupCheckbox"
            />
            {group.name.length > 40 ? (
              <OverlayTrigger
                rootClose={false}
                trigger={["hover", "focus"]}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-${group.id}`}>
                    {group.name}
                  </Tooltip>
                }
              >
                <div className="groupNameEllipsis">
                  {group.name}
                </div>
              </OverlayTrigger>
            ) : (
              <div className="groupNameEllipsis">
                {group.name}
              </div>
            )}
          </div>
        </div>
        {(filterStudents(group) || []).length > 0 ? (
          <>
            <div
              className="studentBadge"
              ref={badgeRef}
              onClick={(e) => {
                e.stopPropagation();
                setPopoverOpenId(isPopoverOpen ? null : group.id);
              }}
            >
              {filterStudents(group).length}
            </div>
            <Overlay
              show={isPopoverOpen}
              target={badgeRef.current}
              placement="left"
              rootClose
              onHide={() => setPopoverOpenId(null)}
              containerPadding={8}
            >
              <Popover id={`students-${group.id}`} className="studentsPopover">
                <Popover.Content>
                  <div className="popoverHeader">
                    {urlTab == "active" ? "Students With Goal" : "Students With Archived Goal"}
                  </div>

                  <div className="studentList" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {(filterStudents(group) || []).map((student) => (
                      <div key={student.id} className="studentItem">
                        {student.first_name} {" "}
                        {student.last_name}
                      </div>
                    ))}
                  </div>
                </Popover.Content>
              </Popover>
            </Overlay>
          </>
        ) : null}
      </div>
  );
};

const GroupReorderModal: FunctionComponent<Props> = () => {
  const dispatch = useDispatch();
  const { groupReorderSorting } = useInterventionGroupReorder();
  const { isDistrictOwner } = useUserRole();
  const [activeId, setActiveId] = useState<number | null>(null);
  const userInfo = useSelector(
    (s: ApplicationState) => s.auth.userInfo
  );
  const currentRole = useSelector(
    (s: ApplicationState) => s.auth.userInfo?.profile.current_assignment?.role
  );
  const interventionGroupsForReorder =
    useSelector(
      (s: ApplicationState) =>
        s.onboarding.interventionGroupsForReorder
    ) || [];

  const selectedCaseIdForGroupReorder = useSelector(
    (s: ApplicationState) =>
      s.onboarding.selectedCaseIdForGroupReorder
  );

  const show = useSelector(
    (s: ApplicationState) =>
      s.onboarding.modalsState.showGroupReorderModal
  );
  

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const { isDesktop } = useDeviceDetect();

  /**
   * Existing API object if already saved
   * Example:
   * user.group_order_config
   */
  const existingGroupOrderConfig =
    useSelector(
      (s: ApplicationState) =>
        s.auth?.userInfo?.profile.additional_info?.group_order
    ) || {};

  const [groups, setGroups] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [popoverOpenId, setPopoverOpenId] = useState<number | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const initialOrderRef = React.useRef<number[] | null>(null);
  const groupListRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!show) return;
    setLoadingGroups(true);
    const sortedGroups =
      groupReorderSorting(
        interventionGroupsForReorder,
        selectedCaseIdForGroupReorder!
      ).map((g: InterventionGroup | GroupRecommendation, idx: number) => ({
        ...g,
        _originalIndex: idx,
      }));

    setGroups(sortedGroups);
    setSelectedIds([]);

    initialOrderRef.current = sortedGroups.map(
      (g: InterventionGroup | GroupRecommendation) => g.id!
    );

    setIsDirty(false);
  }, [
    show,
    interventionGroupsForReorder,
    selectedCaseIdForGroupReorder,
  ]);

  useEffect(() => {
    const container = groupListRef.current;

    if (!container) return;

    const handleScroll = () => {
      setPopoverOpenId(null);
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);

  useEffect(() => {
    setPopoverOpenId(null);
    if(loadingGroups){
      const timeout = setTimeout(() => {
        setLoadingGroups(false)
      }, groups.length < 150 ? 500 : 700);
      return () => clearTimeout(timeout);
    }
  }, [groups]);

  const handleHideModal = useCallback(() => {
    setPopoverOpenId(null);
    const initial = (
      groupReorderSorting(
        interventionGroupsForReorder, 
        selectedCaseIdForGroupReorder!
      ) || []
    ).map((g: InterventionGroup, idx: number) => ({
       ...g,
       _originalIndex: idx
    }));
    setGroups(initial);
    dispatch(hideGroupReorderModal());
  }, [dispatch]);

  const handleDragStart = ({ active }: any) => {
    setActiveId(Number(active.id));
    // close any open popover when dragging starts
    setPopoverOpenId(null);
  };

  const draggedSelectedItems = useMemo(() => {
    if (!activeId) return [];

    /**
     * If active item is selected,
     * drag all selected items
     */
    if (selectedIds.includes(activeId)) {
      return groups.filter((g) =>
        selectedIds.includes(g.id)
      );
    }

    /**
     * Otherwise drag only active item
     */
    return groups.filter(
      (g) => g.id === activeId
    );
  }, [activeId, selectedIds, groups]);

  const handleDragEnd = ({
    active,
    over,
  }: any) => {
    setActiveId(null);
    if (!over || active.id === over.id)
      return;
    const oldIndex = groups.findIndex(
      (g) => g.id === active.id
    );
    const newIndex = groups.findIndex(
      (g) => g.id === over.id
    );

    /**
     * MULTI DRAG
     */
    if (
      selectedIds.includes(Number(active.id)) &&
      selectedIds.length > 1
    ) {
      const activeId = Number(active.id);
      const overId = Number(over.id);

      const remainingItems = groups.filter(
        (g) => !selectedIds.includes(g.id)
      );

      /**
       * Find indexes from ORIGINAL list
       */
      const activeIndex = groups.findIndex(
        (g) => g.id === activeId
      );

      const overIndex = groups.findIndex(
        (g) => g.id === overId
      );

      /**
       * Find insertion index in remaining list
       */
      let insertIndex =
        remainingItems.findIndex(
          (g) => g.id === overId
        );

      /**
       * DRAGGING DOWNWARD
       * Insert AFTER hovered item
       */
      if (activeIndex < overIndex) {
        insertIndex += 1;
      }

      const updated = [
        ...remainingItems.slice(
          0,
          insertIndex
        ),

        ...draggedSelectedItems,

        ...remainingItems.slice(
          insertIndex
        ),
      ];

      setGroups(updated);
      setIsDirty(true);
      return;
    }

    /**
     * SINGLE DRAG
     */
    setGroups((items) => arrayMove(items, oldIndex, newIndex));
    setIsDirty(true);
  };

  /**
   * Select Single
   */
  const toggleSelect = useCallback((id: number) => {
    setPopoverOpenId(null);
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  }, []);


   /**
    * Detect Order Changes
    */
  const hasChanges = useMemo(() => {
    // consider dirty flag set by user actions (drag/reset) OR actual order difference
    const currentOrder = groups.map((g) => g.id);
    const originalOrder = initialOrderRef.current || interventionGroupsForReorder.map((g: InterventionGroup) => g.id);
    const orderDiffers = JSON.stringify(currentOrder) !== JSON.stringify(originalOrder);
    return isDirty || orderDiffers;
  }, [groups, interventionGroupsForReorder, isDirty]);

  /**
   * Submit
   */
  const handleSubmit = useCallback(async () => {
    // If no changes, inform user and do not call API
    if (!hasChanges) {
      toastr.info("Info", "No changes detected.");
      return;
    }

     /**
      * Start with existing API structure
      */
    const payload: any = {
      reading_groups_page: {
        ...(existingGroupOrderConfig.reading_groups_page ||
          {}),
      },

      behavior_groups_page: {
        ...(existingGroupOrderConfig.behavior_groups_page ||
          {}),
      },
    };

    /**
     * Build Teacher + Page wise ordering
     */
    const isBehavior =
      window.location.pathname.includes(
        "groups/behavior"
      );

    const pageKey = isBehavior
      ? "behavior_groups_page"
      : "reading_groups_page";

    payload[pageKey][
      selectedCaseIdForGroupReorder!
    ] = groups.map((grp: InterventionGroup | GroupRecommendation) => grp.id);

        //setSubmitting(true);
    if (!userInfo) return;
    
    const updatedGroupReorderInfo: UserInfo = {
      ...userInfo,
      profile: {
        ...userInfo.profile,
        additional_info: {
          ...userInfo.profile.additional_info,
          group_order: payload,
        },
      },
    };
    setSubmitting(true);
    dispatch<any>(groupReorderUpdate(updatedGroupReorderInfo)).then(() => {
      toastr.success(
        "Success",
        "Group order updated successfully."
      );
      setSubmitting(false);
      setSelectedIds([]);
      setIsDirty(false);
      handleHideModal();
    }).catch((err: string) => {
      toastr.error(
        "Failed",
        "Failed to update group order. Please try again."
      );
    });
  }, [
    groups,
    selectedIds,
    dispatch,
    existingGroupOrderConfig,
    hasChanges,
  ]);


  return (
    <Modal
      show={show}
      onHide={handleHideModal}
      animation={false}
      backdropClassName="customDarkModalBackdrop inner in"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="groupReorderModal"
      {...(!isDesktop ? { size: "lg" } : {})}
    >
      <Modal.Header
        closeButton
        className="purpleModalHeader"
      >
        <Modal.Title>
          Change Group Order
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="groupReorderBody">
        {loadingGroups ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "320px"}}>
            <LoadingIndicator />
          </div>
        ) : ''} 
        
        {loadingGroups == false ? (
          <div className="groupReorderContainer">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <label>
                Drag and drop groups to reorder them.
              </label>

              {selectedIds.length > 0 ? (
                <div className="d-flex align-items-center gap-2">
                  <span className="group-selection-info">
                    <span className="group-selection-count">
                      {selectedIds.length}
                    </span>
                    selected
                  </span>
                  <OverlayTrigger
                    rootClose={true}
                    trigger={["focus", "hover"]}
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-clear-selection">
                        Clear Selection
                      </Tooltip>
                    }
                  >
                    <span
                      className="redBtnSmFilter"
                      onClick={() => setSelectedIds([])}
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon
                        icon={faSync}
                        size="1x"
                        style={{ verticalAlign: "middle", color: "#fff" }}
                      />
                    </span>
                  </OverlayTrigger>
                </div>
              ) : (
                <span style={{ height: 24 }} />
              )}
            </div>
            {/* LIST */}
            <DndContext
              sensors={sensors}
              collisionDetection={pointerWithin}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={groups.map((g) => g.id)}
                strategy={
                  verticalListSortingStrategy
                }
              >
                <div 
                  className="groupList"
                  ref={groupListRef}
                  onScroll={() => setPopoverOpenId(null)}
                >
                  {groups.map((group) => (
                    <SortableItem
                      key={group.id}
                      group={group}
                      selectedIds={selectedIds}
                      toggleSelect={toggleSelect}
                      activeId={activeId}
                      popoverOpenId={popoverOpenId}
                      setPopoverOpenId={setPopoverOpenId}
                    />
                  ))}
                </div>
              </SortableContext>

              {/* DRAG OVERLAY */}
              <DragOverlay  dropAnimation={null}>
                {activeId ? (
                  <div className="jiraMultiDrag">
                    {draggedSelectedItems
                      .slice(0, 4)
                      .map((g, idx) => (
                        <div
                          key={g.id}
                          className="jiraDragCard"
                          style={{
                            top: `${idx * 6}px`,
                            left: `${idx * 6}px`,
                            zIndex: 100 - idx,
                            backgroundColor: g.finished ? "#00265b" : "#43a047",
                          }}
                        >
                          <div className="overlayCardName">
                            {g.name}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
            <hr/>
            {/* FOOTER */}
            <div className="btnActions">
              <Button
                onClick={handleHideModal}
                className="whiteBtnSm"
                bsPrefix="b"
              >
                Cancel
              </Button>

              <Button
                onClick={handleSubmit}
                disabled={
                  submitting
                }
                bsPrefix="b"
                className="blueBtnSm pointer"
              >
                {submitting ? (
                  <Spinner
                    animation="border"
                    size="sm"
                  />
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </div>
        ) : ''}
      </Modal.Body>
    </Modal>
  );
};

export default GroupReorderModal;